import { useEffect, useRef, useState } from 'react';

import { ApiErrorSchema, SEARCH_JUDGEMENTS, type ClauseSearchHit, type FieldError, type SearchJudgement } from '@clm/shared-types';

import { ApiError } from '../../api/errors';
import { classifyApiError, type ApiProblemKind } from '../../api/problem';
import { recordJudgement } from '../../api/search-quality';
import { ErrorNotice } from '../../components/ErrorNotice';

/**
 * The judgement control under each clause hit — FR-26, NFR-13 · CLM-39.
 *
 * Three buttons: Preferred, Relevant, Not relevant. Each posts
 * `record_search_judgement` with the query as typed, the clause code and the
 * response's `search_id` — always the `search_id`, so the API copies the vector
 * it already logged and no embedding call is made for a judgement recorded from
 * a result row. "Correct clause" (FR-26) is two clicks: Preferred on the right
 * hit, Not relevant on the wrong one.
 *
 * ## Rendered for every caller; the 403 is the honest state
 *
 * `search:tune` is `legal_reviewer` alone (G15). The control never hides itself
 * on a role claim — `hasRole` is display material only — so an admin or a
 * contributor sees the buttons, clicks, and reads the forbidden copy naming the
 * role. In production the CEQ-Legal group id is in the App Service settings and
 * its members resolve to `legal_reviewer`; everyone else gets the 403 by design.
 *
 * ## A submitted judgement is never aborted on unmount
 *
 * The server commits the row and its audit row regardless (CandidatePanel's
 * rule), so the promise is left to settle and only the control's own state
 * updates are skipped once it has gone.
 */

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting'; readonly judgement: SearchJudgement }
  | { readonly kind: 'settled'; readonly judgement: SearchJudgement; readonly audit_id: string }
  | {
      readonly kind: 'problem';
      readonly judgement: SearchJudgement;
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
      readonly field_errors: readonly FieldError[];
    };

const LABEL: Readonly<Record<SearchJudgement, string>> = {
  Preferred: 'Preferred',
  Relevant: 'Relevant',
  NotRelevant: 'Not relevant',
};

function fieldErrorsOf(error: unknown): readonly FieldError[] {
  if (!(error instanceof ApiError)) return [];
  const parsed = ApiErrorSchema.safeParse(error.body);
  return parsed.success ? parsed.data.field_errors : [];
}

export function JudgementControls({
  search_id,
  query_text,
  hit,
}: {
  readonly search_id: string;
  readonly query_text: string;
  readonly hit: ClauseSearchHit;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [changing, setChanging] = useState(false);
  const mountedRef = useRef(true);
  const clauseCode = hit.clause.clause_code;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function judge(judgement: SearchJudgement) {
    setChanging(false);
    setPhase({ kind: 'submitting', judgement });
    void (async () => {
      try {
        const response = await recordJudgement({ query_text, clause_code: clauseCode, judgement, search_id });
        if (!mountedRef.current) return;
        setPhase({ kind: 'settled', judgement, audit_id: response.audit.audit_id });
      } catch (error) {
        if (!mountedRef.current) return;
        setPhase({
          kind: 'problem',
          judgement,
          problem: classifyApiError(error),
          detail: error instanceof ApiError ? error.message : null,
          correlation_id: error instanceof ApiError ? error.correlation_id : null,
          field_errors: fieldErrorsOf(error),
        });
      }
    })();
  }

  const disabled = phase.kind === 'submitting' || (phase.kind === 'settled' && !changing);

  return (
    <div className="judge">
      <div role="group" aria-label={`Judge ${clauseCode} for this query`} className="judge-group">
        {SEARCH_JUDGEMENTS.map((judgement) => (
          <button
            key={judgement}
            type="button"
            className="btn sm"
            id={`jc-${search_id}-${clauseCode}-${judgement}`}
            aria-label={`Mark ${clauseCode} ${judgement} for '${query_text}'`}
            aria-pressed={phase.kind === 'settled' && phase.judgement === judgement}
            disabled={disabled}
            onClick={() => {
              judge(judgement);
            }}
          >
            {LABEL[judgement]}
          </button>
        ))}
      </div>

      {phase.kind === 'submitting' ? (
        <p role="status" aria-busy="true" className="mut">
          Recording {LABEL[phase.judgement]}…
        </p>
      ) : null}

      {phase.kind === 'settled' ? (
        <p role="status" className="mut">
          Recorded {LABEL[phase.judgement]} · audit <code>{phase.audit_id}</code>
          {changing ? null : (
            <>
              {' '}
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setChanging(true);
                }}
              >
                Change
              </button>
            </>
          )}
        </p>
      ) : null}

      {phase.kind === 'problem' ? (
        phase.problem === 'conflict' ? (
          // A same-value re-click: the judgement is already on record. Neutral, not an error.
          <p role="status" className="mut">
            {LABEL[phase.judgement]} is already recorded for this clause and query.
          </p>
        ) : (
          <ErrorNotice
            kind={phase.problem}
            detail={phase.detail}
            correlation_id={phase.correlation_id}
            action={
              phase.problem === 'forbidden' ? (
                <p className="mut">
                  Judging is a CEQ-Legal action (<code>legal_reviewer</code>). Your sign-in was accepted; your role was not.
                </p>
              ) : phase.problem === 'not_implemented' ? (
                <p className="mut">
                  The endpoint is <code>manage-clause</code> (<code>POST /v1/clauses/actions</code>).
                </p>
              ) : phase.problem === 'validation' && phase.field_errors.length > 0 ? (
                <ul className="mut">
                  {phase.field_errors.map((fieldError) => (
                    <li key={`${fieldError.path}-${fieldError.message}`}>
                      <code>{fieldError.path}</code>: {fieldError.message}
                    </li>
                  ))}
                </ul>
              ) : phase.problem === 'unknown' ? (
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    judge(phase.judgement);
                  }}
                >
                  Retry
                </button>
              ) : undefined
            }
          />
        )
      ) : null}
    </div>
  );
}
